import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface SiteSettings {
  site_name: string;
  site_favicon: string;
  footer_contact_email: string;
  footer_contact_phone: string;
  footer_contact_address: string;
  social_instagram: string;
  social_facebook: string;
  social_whatsapp: string;
}

const defaults: SiteSettings = {
  site_name: 'Ma Gádeni',
  site_favicon: '',
  footer_contact_email: 'contato@magadeni.com.br',
  footer_contact_phone: '+55 (81) 99999-9999',
  footer_contact_address: 'Recife, Pernambuco, Brasil',
  social_instagram: 'https://instagram.com',
  social_facebook: 'https://facebook.com',
  social_whatsapp: 'https://wa.me/5581999999999',
};

const settingKeys = Object.keys(defaults) as (keyof SiteSettings)[];

export default function AdminSettingsPage() {
  const [data, setData] = useState<SiteSettings>(defaults);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    const { data: rows } = await supabase
      .from('site_settings')
      .select('key, value')
      .in('key', settingKeys);

    if (rows) {
      const map: Partial<SiteSettings> = {};
      rows.forEach(r => { (map as Record<string, string>)[r.key] = r.value || ''; });
      setData({ ...defaults, ...map });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    for (const key of settingKeys) {
      const { data: existing } = await supabase.from('site_settings').select('id').eq('key', key).maybeSingle();
      if (existing) {
        await supabase.from('site_settings').update({ value: data[key], updated_at: new Date().toISOString() }).eq('key', key);
      } else {
        await supabase.from('site_settings').insert({ key, value: data[key] });
      }
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors";
  const Field = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Configurações Gerais</h1>
          <p className="text-gray-400 text-sm mt-0.5">Informações globais do site</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Identidade do Site</h2>
            <Field label="Nome do Site">
              <input className={inputCls} value={data.site_name} onChange={e => setData({ ...data, site_name: e.target.value })} />
            </Field>
            <Field label="URL do Favicon" hint="Ícone exibido na aba do navegador. Formato .ico ou .png 32x32px">
              <input className={inputCls} value={data.site_favicon} onChange={e => setData({ ...data, site_favicon: e.target.value })} placeholder="https://..." />
            </Field>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Informações de Contato</h2>
            <Field label="E-mail">
              <input className={inputCls} type="email" value={data.footer_contact_email} onChange={e => setData({ ...data, footer_contact_email: e.target.value })} />
            </Field>
            <Field label="Telefone / WhatsApp">
              <input className={inputCls} value={data.footer_contact_phone} onChange={e => setData({ ...data, footer_contact_phone: e.target.value })} />
            </Field>
            <Field label="Endereço">
              <input className={inputCls} value={data.footer_contact_address} onChange={e => setData({ ...data, footer_contact_address: e.target.value })} />
            </Field>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Redes Sociais</h2>
            <Field label="Instagram">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-instagram-line text-gray-400 text-sm"></i>
                </div>
                <input className={`${inputCls} pl-9`} value={data.social_instagram} onChange={e => setData({ ...data, social_instagram: e.target.value })} placeholder="https://instagram.com/..." />
              </div>
            </Field>
            <Field label="Facebook">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-facebook-line text-gray-400 text-sm"></i>
                </div>
                <input className={`${inputCls} pl-9`} value={data.social_facebook} onChange={e => setData({ ...data, social_facebook: e.target.value })} placeholder="https://facebook.com/..." />
              </div>
            </Field>
            <Field label="WhatsApp">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-gray-400 text-sm"></i>
                </div>
                <input className={`${inputCls} pl-9`} value={data.social_whatsapp} onChange={e => setData({ ...data, social_whatsapp: e.target.value })} placeholder="https://wa.me/55..." />
              </div>
            </Field>
          </div>

          <div className="bg-[#111111] rounded-xl p-5">
            <div className="flex gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/15 shrink-0">
                <i className="ri-information-line text-[#C9A84C] text-sm"></i>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Sobre as configurações</p>
                <p className="text-white/40 text-xs mt-1 leading-relaxed">
                  As configurações salvas aqui são usadas em todo o site — footer, contato, redes sociais e SEO. Mantenha sempre atualizado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
